<template>
  <VaNavbar class="app-layout-navbar py-2 px-0">
    <template #left>
      <div class="left">
        <RouterLink to="/" aria-label="Visit home page">
          <Logo :height="32" />
        </RouterLink>
        <Transition v-if="isUserApproved" name="icon-fade" mode="out-in">
          <VaButton
            preset="secondary"
            class="sidebar-toggle-btn"
            :class="isSidebarMinimized ? 'icon-flipped' : ''"
            icon="menu_open"
            :aria-label="isSidebarMinimized ? 'Open sidebar' : 'Close sidebar'"
            color="primary"
            size="medium"
            @click="isSidebarMinimized = !isSidebarMinimized"
          />
        </Transition>
      </div>
    </template>
    <template #right>
      <div class="app-navbar-actions">
        <ThemeSwitcher class="app-navbar-actions__item" />
        <AppNavbarActions class="app-navbar__actions" :is-mobile="isMobile" />
      </div>
    </template>
  </VaNavbar>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '../../stores/global-store'
import { useAuthStore } from '../../stores/auth'
import { isApproved } from '../../utils/approvalUtils'
import ThemeSwitcher from '../theme-switcher/ThemeSwitcher.vue'
import AppNavbarActions from './components/AppNavbarActions.vue'
import Logo from '../Logo.vue'

defineProps({
  isMobile: { type: Boolean, default: false },
})

const GlobalStore = useGlobalStore()
const authStore = useAuthStore()

const { isSidebarMinimized } = storeToRefs(GlobalStore)

// Check if user is approved - only show sidebar toggle for approved users
const isUserApproved = computed(() => {
  return isApproved(authStore.memberDetails?.approvalStatus)
})
</script>

<style lang="scss" scoped>
.va-navbar {
  z-index: 2;

  @media screen and (max-width: 950px) {
    .left {
      width: 100%;
    }

    .app-navbar__actions {
      display: flex;
      justify-content: space-between;
    }
  }
}

.app-navbar-actions {
  display: flex;
  align-items: center;

  .va-dropdown__anchor {
    color: var(--va-primary);
    fill: var(--va-primary);
  }

  &__item {
    padding: 0;
    margin-left: 0.25rem;
    margin-right: 0.25rem;

    :deep(svg) {
      height: 20px;
    }

    &--profile {
      display: flex;
      justify-content: center;
    }

    .va-dropdown-content {
      background-color: var(--va-white);
    }

    :deep(.va-list-separator) {
      background-color: var(--va-background-border) !important;
    }

    :deep(.va-dropdown .va-list-separator) {
      background-color: var(--va-background-border) !important;
    }

    :deep(.va-dropdown-content .va-list-separator) {
      background-color: var(--va-background-border) !important;
    }

    @media screen and (max-width: 640px) {
      margin-left: 0;
      margin-right: 0;

      &:first-of-type {
        margin-left: 0;
      }
    }
  }
}

.left {
  display: flex;
  align-items: center;
  margin-left: 0.5rem;

  & > * {
    margin-right: 1rem;
  }

  & > *:last-child {
    margin-right: 0;
  }
}

.sidebar-toggle-btn {
  padding: 8px !important;
  min-width: auto !important;

  :deep(.va-button__content) {
    padding: 0;
  }

  &:hover {
    background-color: var(--va-background-element);
  }

  // Responsive sizing
  @media (max-width: 768px) {
    padding: 12px !important;

    :deep(.va-icon) {
      font-size: 1.5rem;
    }
  }
}

.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: transform 0.5s ease;
}

.icon-fade-enter,
.icon-fade-leave-to {
  transform: scale(0.5);
}

:deep(.icon-flipped .va-icon) {
  transform: scaleX(-1);
}
</style>
